import { useRouter } from 'next/router';
import { ParsedUrlQueryInput } from 'querystring';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { SOCIAL_PLATFORMS } from 'ui-components';
import { Creator } from '~/clientApi/creator.model';
import { FEATURE_FLAGS } from '~/constants';
import { IContentSearchQuery } from '~/hooks/useContentSearch';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';

import { removeEmptyUrlParams } from './SearchContent.util';
import { useSearchContentParams } from './useSearchContentParams';

export const ContentSearchFilterContext = createContext<{
  filters: IContentSearchQuery;
  onChange: (filters: IContentSearchQuery) => void;
  isGlobalContentFiltersEnabled: boolean;
  globalContentFiltersFFStatus: 'idle' | 'loading' | 'error';
  selectedCreator: Creator | undefined;
  setSelectedCreator: Dispatch<SetStateAction<Creator | undefined>>;
  size: number;
  clearFilters?: () => void;
}>({
  filters: { platform: [] },
  onChange: () => {},
  isGlobalContentFiltersEnabled: false,
  globalContentFiltersFFStatus: 'idle',
  selectedCreator: undefined,
  setSelectedCreator: () => {},
  size: 0,
  clearFilters: () => {},
});

export const useContentSearchFilterContext = () => {
  return useContext(ContentSearchFilterContext);
};

interface ContentSearchFilterProviderProps {
  children: React.ReactNode;
  pathname: string;
  keepQueryString?: boolean;
}

export const ContentSearchFilterProvider: React.FC<
  ContentSearchFilterProviderProps
> = ({ children, pathname, keepQueryString = false }) => {
  const router = useRouter();
  const initialFiltersFromUrl = useSearchContentParams();
  const [filters, setFilters] = useState<IContentSearchQuery>({
    ...initialFiltersFromUrl,
    platform: initialFiltersFromUrl.platform || [],
  });

  useEffect(() => {
    // Fixes an issue where the search input retains its previous value when filters are applied
    setFilters({
      ...initialFiltersFromUrl,
      platform: initialFiltersFromUrl.platform || [],
    });
  }, [initialFiltersFromUrl]);

  const handleChange = (newFilters: IContentSearchQuery) => {
    setFilters((prevFilters) => {
      const updatedFilters = {
        ...prevFilters,
        ...newFilters,
      };
      /**
       * We use URL state only to reflect local state for URL sharing/reloading.
       * Main thread remains unblocked on route change.
       *
       */
      setTimeout(() => {
        router.push(
          {
            pathname,
            /**
             * The `keepQueryString` flag is used to fix the issue with a redirection on the search page.
             * We need the parameters that were previously in the URL, such as `tab`, to avoid this problem.
             * Maybe in the future, we can improve this part, but for now, we will consider it a temporary fix.
             */
            query: keepQueryString
              ? { ...router.query, ...updatedFilters }
              : (removeEmptyUrlParams(updatedFilters) as ParsedUrlQueryInput),
          },
          undefined,
          { shallow: true },
        );
      });
      return updatedFilters;
    });
  };

  const { isEnabled: isGlobalContentFiltersEnabled, status: featureStatus } =
    useFeature(FEATURE_FLAGS.foamContentGlobalFilters);

  const globalContentFiltersFFStatus =
    featureStatus === 'success' ? 'idle' : featureStatus;

  const [selectedCreator, setSelectedCreator] = useState<Creator | undefined>();

  const getFilterSize = useCallback(() => {
    let count = 0;

    const { platform, channel, creator, ads } = filters;

    if (platform?.includes(SOCIAL_PLATFORMS.instagram) && channel) {
      count += platform.length - 1 + channel.length;
    } else if (platform) {
      count += platform.length;
    }

    if (creator) count++;
    if (ads) count++;

    return count;
  }, [filters]);

  const clearFilters = useCallback(async () => {
    const { query } = router;
    const {
      ads: _ads,
      creator: _creator,
      platform: _platform,
      channel: _channels,
      ...searchQuery
    } = query as IContentSearchQuery;

    setFilters({ ...searchQuery, platform: [] });

    await router.push({
      pathname,
      query: { ...searchQuery, platform: [] },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query]);

  return (
    <ContentSearchFilterContext.Provider
      value={{
        filters: filters,
        onChange: handleChange,
        isGlobalContentFiltersEnabled,
        globalContentFiltersFFStatus,
        selectedCreator,
        setSelectedCreator,
        size: getFilterSize(),
        clearFilters,
      }}
    >
      {children}
    </ContentSearchFilterContext.Provider>
  );
};
